//
// Copyright 2025 DXOS.org
//

import { useControllableState } from '@radix-ui/react-use-controllable-state';
import type * as Schema from 'effect/Schema';
import type * as SchemaAST from 'effect/SchemaAST';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type AnyProperties } from '@dxos/echo/internal';
import { SchemaEx } from '@dxos/effect';
import { log } from '@dxos/log';
import { useDefaultValue } from '@dxos/react-ui';
import { type ValidationError, validateSchema } from '@dxos/schema';
import { type MaybePromise } from '@dxos/util';

import { type FormFieldStatus } from '#types';

/**
 * Form properties.
 */
export interface FormHandlerProps<T extends AnyProperties> {
  /**
   * Effect schema (Type literal).
   */
  schema?: Schema.Schema<T, any>;

  /**
   * Values for the form if controlled.
   */
  values?: Partial<T>;

  /**
   * Default values for the form which will be used if values is not provided.
   */
  defaultValues?: Partial<T>;

  /**
   * Auto-save the form when the values change.
   */
  autoSave?: boolean;

  /**
   * Callback for value changes. Note: This is called even when values are invalid.
   * Sometimes the parent component may want to know about changes even if the form is
   * in an invalid state.
   */
  onValuesChanged?: (values: Partial<T>, meta: FormUpdateMeta<T>) => void;

  /**
   * Custom validation function that runs only after schema validation passes.
   * Use this for complex validation logic that can't be expressed in the schema.
   * @returns Array of validation errors, or undefined if validation passes
   */
  onValidate?: (values: T) => ValidationError[] | undefined;

  /**
   * Called when the form is submitted and passes validation.
   */
  onSave?: (values: T, meta: FormUpdateMeta<T>) => MaybePromise<void>;

  /**
   * Called when the form is canceled.
   */
  onCancel?: () => void;
}

export type FormUpdateMeta<T extends AnyProperties> = {
  changed: FormHandler<T>['changed'];
  isValid: boolean;
};

/**
 * Form handler properties and methods.
 */
export type FormHandler<T extends AnyProperties> = Pick<FormHandlerProps<T>, 'schema' | 'autoSave'> & {
  /** Initial values (which may not pass validation). */
  values: Partial<T>;

  /** Fields whose value has been edited (set by `onValueChange`). Gates autosave-on-blur. */
  changed: Record<SchemaEx.JsonPath, boolean>;
  /** Fields the user has interacted with (set by `onBlur`). Gates error *visibility* — errors only surface once touched. */
  touched: Record<SchemaEx.JsonPath, boolean>;

  /** Map of error strings. */
  errors: Record<SchemaEx.JsonPath, string>;

  /** Whether the values pass validation (no errors at all). */
  isValid: boolean;
  /**
   * Whether the submit affordance should be enabled. Stricter than `isValid`: it also requires no save
   * in flight (`saving`) and only blocks on errors for *touched* fields, so a pristine form with
   * not-yet-touched required fields can still show an enabled control.
   */
  canSave: boolean;

  onSave: () => void;
  onCancel: () => void;

  //
  // Form field state management
  //

  getStatus: (path: string | (string | number)[]) => FormFieldStatus;
  getValue: <V>(path: (string | number)[]) => V | undefined;
  onBlur: (path: (string | number)[]) => void;
  onValueChange: <V>(path: (string | number)[], type: SchemaAST.AST, value: V) => void;
};

/**
 * Creates a hook for managing form state, including values, validation, and submission.
 * Deeply integrated with `@dxos/schema` for schema-based validation.
 */
export const useFormHandler = <T extends AnyProperties>({
  schema,
  autoSave,
  values: valuesProp,
  defaultValues: defaultValuesProp,
  onValuesChanged,
  onValidate,
  onSave,
  onCancel,
  ...props
}: FormHandlerProps<T>): FormHandler<T> => {
  const [changed, setChanged] = useState<Record<SchemaEx.JsonPath, boolean>>({});
  const [touched, setTouched] = useState<Record<SchemaEx.JsonPath, boolean>>({});
  const [errors, setErrors] = useState<Record<SchemaEx.JsonPath, string>>({});
  const [saving, setSaving] = useState(false);
  const defaultValues = useDefaultValue<Partial<T>>(defaultValuesProp, () => ({}));
  const [values$, setValues] = useControllableState<Partial<T>>({
    prop: valuesProp,
    defaultProp: defaultValues,
    onChange: () => {
      // Only reset when controlled: parent passed new values.
      // Uncontrolled updates come from setValues in onValueChange.
      if (valuesProp !== undefined) {
        setValues(valuesProp ?? defaultValues);
        setChanged({});
        setTouched({});
        setErrors({});
        setSaving(false);
      }
    },
  });
  // TODO(wittjosiah): Upgrade @radix-ui/react-use-controllable-state.
  const values = values$ as Partial<T>;

  // Validate.
  const validate = useCallback(
    (values: Partial<T>): values is T => {
      if (!schema) {
        return false;
      }

      let errors: ValidationError[] = validateSchema(schema, values) ?? [];
      log('validate', { values, errors });
      if (errors.length === 0 && onValidate) {
        const validatedValues = values as T;
        errors = onValidate(validatedValues) ?? [];
      }

      setErrors(flatMap(errors));
      const valid = errors.length === 0;
      return valid;
    },
    [schema, onValidate],
  );

  // Validate on schema change.
  const [schemaChanged, setSchemaChanged] = useState(false);
  useEffect(() => setSchemaChanged(true), [schema]);
  useEffect(() => {
    if (schemaChanged) {
      validate(values);
      setSchemaChanged(false);
    }
  }, [validate, values, schemaChanged]);

  const isValid = useMemo(() => {
    return Object.keys(errors).length === 0;
  }, [errors]);

  /**
   * NOTE: We can submit if there is no touched field that has an error.
   * If there's a validation message visible in the form, submit should be disabled.
   */
  const canSave = useMemo(
    () =>
      !saving &&
      // Check if any error paths that are touched have errors.
      !Object.entries(touched).some(
        ([path, isTouched]) =>
          isTouched &&
          Object.keys(errors).some(
            (errorPath) => errorPath === path || errorPath.startsWith(`${path}.`) || errorPath.startsWith(`${path}[`),
          ),
      ),
    [touched, errors, saving],
  );

  const handleSave = useCallback(async () => {
    if (validate(values)) {
      setSaving(true);
      try {
        await onSave?.(values, { changed, isValid });
      } finally {
        setSaving(false);
      }
    }
  }, [values, validate, onSave, changed, isValid]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  //
  // Fields.
  //

  const getStatus = useCallback<FormHandler<T>['getStatus']>(
    (path) => {
      const jsonPath = Array.isArray(path) ? SchemaEx.createJsonPath(path) : path;
      const [_, error] =
        Object.entries(errors).find(
          ([errorPath]) =>
            errorPath === jsonPath || errorPath.startsWith(`${jsonPath}.`) || errorPath.startsWith(`${jsonPath}[`),
        ) ?? [];

      // Only show errors for touched fields.
      const isTouched = touched[jsonPath as SchemaEx.JsonPath];
      if (!isTouched) {
        return {
          status: undefined,
          error: undefined,
        };
      }

      return {
        status: error ? 'error' : undefined,
        error: error ? (error ?? undefined) : undefined,
      };
    },
    [errors, touched],
  );

  const getValue = useCallback<FormHandler<T>['getValue']>(
    (path) => {
      return SchemaEx.getValue(values, SchemaEx.createJsonPath(path));
    },
    [values],
  );

  const onValueChange = useCallback<FormHandler<T>['onValueChange']>(
    (path, type, value) => {
      log('onValueChange', { path, value });

      const jsonPath = SchemaEx.createJsonPath(path);
      const pathArray = path;
      let parsedValue = value as any;
      try {
        if (type._tag === 'NumberKeyword') {
          parsedValue = parseFloat(value as string) || 0;
        }
      } catch (err) {
        log.catch(err);
        parsedValue = undefined;
      }

      // Create merged values for validation and callback. Form never mutates; parent applies via onValuesChanged.
      const newValues = mergeAtPath(values, pathArray as (string | number)[], parsedValue) as Partial<T>;

      // TODO(burdon): Check value has changed from original.
      const newChanged = { [jsonPath]: true };
      setChanged((prev) => ({ ...prev, ...newChanged }));

      // Validate.
      const isValid = validate(newValues);

      // Notify parent; parent is responsible for applying the change.
      onValuesChanged?.(newValues, { isValid, changed: newChanged });

      // For uncontrolled mode, update internal state. Controlled mode relies on parent to pass new values.
      if (valuesProp === undefined) {
        setValues(newValues);
      }
    },
    [values, validate, onValuesChanged, valuesProp],
  );

  const onBlur = useCallback(
    async (path: (string | number)[]) => {
      const jsonPath = SchemaEx.createJsonPath(path);

      // TODO(burdon): Check value has changed from original.
      setTouched((touched) => ({ ...touched, [jsonPath]: true }));
      const isValid = validate(values);

      // Auto-save when a field is blurred and is valid.
      if (Object.keys(changed).length > 0 && isValid && autoSave) {
        await onSave?.(values as T, { changed, isValid });
      }
    },
    [validate, values, changed, autoSave, onSave],
  );

  return useMemo<FormHandler<T>>(
    () => ({
      // State.
      schema,
      values,
      errors,
      touched,
      changed,
      isValid,
      canSave,
      autoSave,

      // Actions.
      onSave: handleSave,
      onCancel: handleCancel,

      // Field utils.
      getStatus,
      getValue,
      onBlur,
      onValueChange,

      ...props,
    }),
    [
      schema,
      autoSave,
      values,
      errors,
      touched,
      changed,
      canSave,
      isValid,
      getStatus,
      getValue,
      onBlur,
      onValueChange,
      handleSave,
      handleCancel,
    ],
  );
};

/**
 * Creates a new object with value at path. Does not mutate source.
 * Preserves arrays when path segments are numeric indices.
 */
const mergeAtPath = (obj: any, path: readonly (string | number)[], value: any): any => {
  if (path.length === 0) {
    return value;
  }

  const [head, ...rest] = path;
  const child = rest.length === 0 ? value : mergeAtPath(obj?.[head], rest, value);

  if (Array.isArray(obj)) {
    const copy = [...obj];
    copy[head as number] = child;
    return copy;
  }

  return { ...obj, [head]: child };
};

const flatMap = (errors: ValidationError[]) => {
  return errors.reduce(
    (result, { path, message }) => {
      // Convert the validation error path format to our SchemaEx.JsonPath format.
      const jsonPath = SchemaEx.fromEffectValidationPath(path);
      if (!(jsonPath in result)) {
        result[jsonPath] = message;
      }
      return result;
    },
    {} as Record<SchemaEx.JsonPath, string>,
  );
};
